import React, { useCallback, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { autoUpdate } from '@floating-ui/react';
import { flip } from '@floating-ui/react';
import { FloatingPortal } from '@floating-ui/react';
import { offset } from '@floating-ui/react';
import { shift } from '@floating-ui/react';
import { useDismiss } from '@floating-ui/react';
import { useFloating } from '@floating-ui/react';
import { useInteractions } from '@floating-ui/react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { MyOrganizationsDatabasesAndRevisions } from 'libs/end-to-end/src/types/me/my-organizations-databases-and-revisions';
import { Organization } from 'libs/end-to-end/src/types/organizations/organization';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useSelectedOrganizationId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedOrganizationId';
import { useSelectedDatabaseId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseId';
import { useSelectedDatabaseRevisionId } from 'apps/sigma2-ui/src/lib/hooks/useSelectedDatabaseRevisionId';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { TypographyVariant } from 'apps/sigma2-ui/src/components/ui/Typography';
import { IconArrowTopSvg } from 'apps/sigma2-ui/src/components/svg/IconArrowTop';
import { Database } from 'libs/end-to-end/src/types/databases/database';
import { ReadPatternHook } from 'apps/sigma2-ui/src/lib/hooks/useReadPattern';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { useResponsive } from 'apps/sigma2-ui/src/lib/hooks/useResponsive';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { useLocation } from 'react-router';
import { getOrganizationLogoUrl } from 'apps/sigma2-ui/src/organizations/utils/get-organization-logo-url';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useMyOrganizationsDatabasesAndRevisions } from 'apps/sigma2-ui/src/lib/hooks/useMyOrganizationsDatabasesAndRevisions';
import { useOnClickOutside } from 'apps/sigma2-ui/src/explore-section/hooks/useOnClickOutside';

type OrgSwitcherDatabase = Pick<Database, 'id' | 'name'>;

const useSelectedOrganizationDatabaseAndRevision = ({
  myOrganizationsDatabasesAndRevisions,
}: {
  myOrganizationsDatabasesAndRevisions: Nullable<MyOrganizationsDatabasesAndRevisions>;
}): {
  selectedOrganization: Nullable<Organization>;
  selectedDatabase: Nullable<OrgSwitcherDatabase>;
  // We don't need the whole revision object, just the ID for validation that it still exists later
  selectedDatabaseRevisionId: Nullable<string>;
} => {
  const selectedOrganizationId = useSelectedOrganizationId();
  const selectedDatabaseId = useSelectedDatabaseId();
  const selectedDatabaseRevisionId = useSelectedDatabaseRevisionId();

  const selectedOrganization = useMemo(() => {
    if (selectedOrganizationId) {
      return (
        myOrganizationsDatabasesAndRevisions?.organizations?.[
          selectedOrganizationId
        ] ?? null
      );
    }

    return null;
  }, [myOrganizationsDatabasesAndRevisions, selectedOrganizationId]);

  const selectedDatabase = useMemo(() => {
    if (selectedOrganization && selectedDatabaseId) {
      return (
        myOrganizationsDatabasesAndRevisions?.organizations?.[
          selectedOrganization.id
        ]?.databases?.[selectedDatabaseId] ?? null
      );
    }

    return null;
  }, [
    myOrganizationsDatabasesAndRevisions,
    selectedOrganization,
    selectedDatabaseId,
  ]);

  return useMemo(() => {
    return {
      selectedOrganization: selectedOrganization ?? null,
      selectedDatabase: selectedDatabase ?? null,
      selectedDatabaseRevisionId: selectedDatabaseRevisionId ?? null,
    };
  }, [selectedOrganization, selectedDatabase, selectedDatabaseRevisionId]);
};

export interface OrgSwitcherHook {
  selectedOrganization: Nullable<Organization>;
  selectedDatabase: Nullable<OrgSwitcherDatabase>;
  organizationsVisible: boolean;
  databasesVisible: boolean;
  highlightedOrganization: Nullable<Organization>;
  myOrganizationsDatabasesAndRevisions: Nullable<MyOrganizationsDatabasesAndRevisions>;
  isLoading: boolean;
  onOrganizationClick: (params: {
    databasesForThisOrg: OrgSwitcherDatabase[];
    organizationId: string;
  }) => void;
  onDatabaseClick: (params: {
    databaseId: string;
    organizationId: string;
  }) => void;
  toggleOrganizationsVisible: () => void;
  hideOrgSwitcher: () => void;
  dismissDatabaseSubmenu: () => void;
}

export interface OrgSwitcherProps extends OrgSwitcherHook {
  isCollapsed: boolean;
  publicDynamicAssetsDomainName: string;
}

export const useOrgSwitcher = (): OrgSwitcherHook => {
  const { myOrganizationsDatabasesAndRevisions, status } =
    useMyOrganizationsDatabasesAndRevisions({
      needsFullRevisions: false,
    });

  const selectedDatabaseId = useSelectedDatabaseId();

  const { selectedOrganization, selectedDatabase } =
    useSelectedOrganizationDatabaseAndRevision({
      myOrganizationsDatabasesAndRevisions,
    });

  const { loadUser } = useUser();
  const navigateSafe = useNavigateSafe();

  const [organizationsVisible, setOrganizationsVisible] = useState(false);

  const [databasesVisible, setDatabasesVisible] = useState(false);

  /**
   * This differs from the selectedOrganization in that it is used to highlight the currently selected organization in the card, but
   * it won't change the selectedOrganization in the search params and fetch the data for the selected organization until the user clicks on a database.
   */
  const [highlightedOrganizationId, setHighlightedOrganizationId] =
    useState<Nullable<string>>(null);

  const hideOrgSwitcher = useCallback(() => {
    setOrganizationsVisible(false);
    setDatabasesVisible(false);
    setHighlightedOrganizationId(null);
  }, [
    setDatabasesVisible,
    setHighlightedOrganizationId,
    setOrganizationsVisible,
  ]);

  const dismissDatabaseSubmenu = useCallback(() => {
    setDatabasesVisible(false);
    setHighlightedOrganizationId(null);
  }, [setDatabasesVisible, setHighlightedOrganizationId]);

  const toggleOrganizationsVisible = useCallback(() => {
    if (organizationsVisible) {
      hideOrgSwitcher();
    } else {
      setOrganizationsVisible(true);
    }
  }, [hideOrgSwitcher, organizationsVisible, setOrganizationsVisible]);

  const { pathname } = useLocation();
  const navigateToOverviewAndLoadUser = useCallback(
    ({
      databaseId,
      organizationId,
    }: {
      databaseId: string;
      organizationId: string;
    }) => {
      // If we seem to be changing databaseId, it's wise to move them to the overview to prevent weird feeling behavior.
      if (selectedDatabaseId !== databaseId && pathname !== '/overview') {
        navigateSafe(`/overview`);
      }

      if (myOrganizationsDatabasesAndRevisions) {
        const databaseRevisionId =
          myOrganizationsDatabasesAndRevisions.organizations[organizationId]
            .databases[databaseId].revisions.latest.id;

        loadUser({
          observation: {
            databaseId,
            type: 'db-revision',
            databaseRevisionId,
          },
          organizationId,
        });
      }
    },
    [
      loadUser,
      myOrganizationsDatabasesAndRevisions,
      navigateSafe,
      pathname,
      selectedDatabaseId,
    ],
  );

  const onDatabaseClick: OrgSwitcherProps['onDatabaseClick'] = useCallback(
    ({ databaseId, organizationId }) => {
      navigateToOverviewAndLoadUser({ databaseId, organizationId });
      hideOrgSwitcher();
    },
    [navigateToOverviewAndLoadUser, hideOrgSwitcher],
  );

  const highlightOrganization = useCallback(
    ({ organizationId }: { organizationId: string }) => {
      setHighlightedOrganizationId(organizationId);
    },
    [],
  );

  const highlightedOrganization = useMemo(() => {
    // If we've selected a new organization in the card, highlight that org. But if not, we should highlight the selected organization that the data is actively representing.
    const id = highlightedOrganizationId ?? selectedOrganization?.id;

    if (!id) {
      return null;
    }

    const organization =
      myOrganizationsDatabasesAndRevisions?.organizations[id];

    if (!organization) {
      return null;
    }

    return { id: organization.id, name: organization.name };
  }, [
    highlightedOrganizationId,
    selectedOrganization,
    myOrganizationsDatabasesAndRevisions,
  ]);

  const onOrganizationClick = useCallback(
    ({
      organizationId,
      databasesForThisOrg,
    }: {
      organizationId: string;
      databasesForThisOrg: OrgSwitcherDatabase[];
    }) => {
      const hasMoreThanOneDatabaseForThisOrg = databasesForThisOrg.length > 1;

      // We still have to pick the database, just highlight it and toggle the databases card
      if (hasMoreThanOneDatabaseForThisOrg) {
        highlightOrganization({ organizationId });
        setDatabasesVisible(true);
      } else {
        const onlyDatabaseForThisOrg = databasesForThisOrg[0];

        if (onlyDatabaseForThisOrg) {
          highlightOrganization({ organizationId });

          // We have everything we need since there's only one database, so just load it
          onDatabaseClick({
            databaseId: databasesForThisOrg[0].id,
            organizationId,
          });
        }
      }
    },
    [highlightOrganization, onDatabaseClick, setDatabasesVisible],
  );

  return useMemo(() => {
    return {
      organizationsVisible: organizationsVisible,
      databasesVisible,
      isLoading: status === 'loading',
      highlightedOrganization,
      selectedOrganization: selectedOrganization ?? null,
      selectedDatabase: selectedDatabase ?? null,
      toggleOrganizationsVisible,
      hideOrgSwitcher,
      dismissDatabaseSubmenu,
      onDatabaseClick,
      onOrganizationClick,
      myOrganizationsDatabasesAndRevisions,
    };
  }, [
    databasesVisible,
    dismissDatabaseSubmenu,
    hideOrgSwitcher,
    highlightedOrganization,
    myOrganizationsDatabasesAndRevisions,
    onDatabaseClick,
    onOrganizationClick,
    organizationsVisible,
    selectedDatabase,
    selectedOrganization,
    status,
    toggleOrganizationsVisible,
  ]);
};

type ButtonAndCardVariant = 'organization' | 'database';

const OrgSwitcherCard: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  return (
    <div
      className={cn(
        'h-fit w-max overflow-hidden bg-white rounded-md ml-2 shadow-4px min-w-44',
        className,
      )}
    >
      {children}
    </div>
  );
};

const variantMap: Record<ButtonAndCardVariant, TypographyVariant> = {
  organization: 'small-label-alt',
  database: 'small-label',
};

type OrgSwitcherCardButtonProps = {
  onClick: () => void;
  text: string;
  icon?: React.ReactNode;
  isFirst?: boolean;
  isLast?: boolean;
  isHighlighted: boolean;
  variant: ButtonAndCardVariant;
};

const OrgSwitcherCardButton = React.forwardRef<
  HTMLButtonElement,
  OrgSwitcherCardButtonProps
>(function OrgSwitcherCardButton(
  { icon, variant, text, isHighlighted, isFirst, isLast, onClick },
  ref,
) {
  const language = useLanguage();

  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn('p-2 text-left border-l-2 w-full', {
        'bg-royal-200 border-l-royal-800': isHighlighted,
        'bg-white border-l-transparent hover:bg-royal-100': !isHighlighted,
        'rounded-t-md': isFirst,
        'rounded-b-md': isLast,
      })}
    >
      <Spacing direction="horizontal" size="xs" className="justify-between">
        <S2Typography
          translatedContent={language.dangerousUserInput({ value: text })}
          variant={variantMap[variant]}
        />

        {icon}
      </Spacing>
    </button>
  );
});

const RightArrow: React.FC = () => {
  return <IconArrowTopSvg svg={{ className: 'w-4 h-4 rotate-90' }} />;
};

export const OrgSwitcherNavigationButton: React.FC<{
  onClick: () => void;
  selectedOrganization: Nullable<Organization>;
  isLoading: boolean;
  isCollapsed: boolean;
  publicDynamicAssetsDomainName: string;
}> = ({
  onClick,
  isLoading,
  isCollapsed,
  selectedOrganization,
  publicDynamicAssetsDomainName,
}) => {
  const language = useLanguage();
  const iconBgSizeClasses = useResponsive(['w-6 h-6', { '2xl': 'w-6 h-6' }]);
  const iconTypographySizeClasses = useResponsive<TypographyVariant>([
    'body-7',
    { '2xl': 'body-6' },
  ]);

  if (isLoading) {
    return (
      <div
        className={cn(
          'py-2 bg-gray-100 w-full flex justify-center items-center',
        )}
      >
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div className="pl-1  bg-gray-100 w-full">
      <button onClick={onClick} className="py-2 px-4  text-left w-full">
        <Spacing
          direction="horizontal"
          size="sm"
          className="justify-between items-center"
        >
          <div
            className={cn(
              `flex space-x-3  font-sans items-center`,
              isCollapsed && 'justify-center',
            )}
          >
            {selectedOrganization && (
              <div
                className={cn(
                  'rounded-full bg-gray-300 flex items-center justify-center',
                  iconBgSizeClasses,
                )}
              >
                {selectedOrganization.logoUrl &&
                selectedOrganization.logoMimeType ? (
                  <img
                    src={getOrganizationLogoUrl({
                      organization: selectedOrganization,
                      publicDynamicAssetsDomainName,
                    })}
                    alt={selectedOrganization.name}
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <S2Typography
                    variant={iconTypographySizeClasses}
                    translatedContent={language.dangerousUserInput({
                      value: selectedOrganization.name.substring(0, 1),
                    })}
                    textColorVariant="white"
                  />
                )}
              </div>
            )}

            {!isCollapsed && (
              <span className="truncate max-w-28">
                <S2Typography
                  variant={
                    !selectedOrganization ? 'small-label-alt' : 'nav-body-7-alt'
                  }
                  translatedContent={language.orgSwitcherOrgSelectionLabel({
                    orgName: selectedOrganization?.name,
                  })}
                />
              </span>
            )}
          </div>

          {(!isCollapsed || !selectedOrganization) && <RightArrow />}
        </Spacing>
      </button>
    </div>
  );
};

const getDatabasesForOrganization = ({
  organizationId,
  myOrganizationsDatabasesAndRevisions,
}: {
  organizationId: string;
  myOrganizationsDatabasesAndRevisions: Nullable<MyOrganizationsDatabasesAndRevisions>;
}): OrgSwitcherDatabase[] => {
  const databasesObj =
    myOrganizationsDatabasesAndRevisions?.organizations?.[organizationId]
      ?.databases ?? {};

  return Object.values(databasesObj);
};

export const OrgSwitcher: React.FC<OrgSwitcherProps> = ({
  organizationsVisible,
  databasesVisible,
  highlightedOrganization,
  selectedOrganization,
  selectedDatabase,
  isLoading,
  toggleOrganizationsVisible,
  hideOrgSwitcher,
  dismissDatabaseSubmenu,
  onDatabaseClick,
  onOrganizationClick,
  myOrganizationsDatabasesAndRevisions,
  isCollapsed,
  publicDynamicAssetsDomainName,
}) => {
  const organizations = Object.values(
    myOrganizationsDatabasesAndRevisions?.organizations ?? {},
  );

  const numOrganizations = organizations.length;

  const databasesForHighlightedOrganization = getDatabasesForOrganization({
    organizationId: highlightedOrganization?.id ?? '',
    myOrganizationsDatabasesAndRevisions,
  });

  const numDatabasesForHighlightedOrganization =
    databasesForHighlightedOrganization.length;

  const databaseSubmenuOpen =
    organizationsVisible &&
    databasesVisible &&
    highlightedOrganization != null &&
    numDatabasesForHighlightedOrganization > 1;

  const onDatabaseSubmenuOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        dismissDatabaseSubmenu();
      }
    },
    [dismissDatabaseSubmenu],
  );

  const { refs, floatingStyles, context } = useFloating({
    open: databaseSubmenuOpen,
    onOpenChange: onDatabaseSubmenuOpenChange,
    placement: 'right-start',
    middleware: [
      offset(8),
      flip({
        fallbackPlacements: ['left-start', 'right-end', 'left-end'],
      }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const dismiss = useDismiss(context);

  const { getFloatingProps } = useInteractions([dismiss]);

  const databaseFloatingDomRef = useRef<HTMLDivElement | null>(null);

  const orgSwitcherOutsideExtraRoots = useMemo(
    () => [databaseFloatingDomRef],
    [],
  );

  const orgSwitcherRef = useOnClickOutside({
    handleClickOutside: () => {
      if (organizationsVisible) {
        hideOrgSwitcher();
      }
    },
    extraRoots: orgSwitcherOutsideExtraRoots,
  });

  return (
    <div ref={orgSwitcherRef}>
      <Spacing direction="horizontal" size="gap-0" className="justify-between">
        <OrgSwitcherNavigationButton
          onClick={toggleOrganizationsVisible}
          selectedOrganization={selectedOrganization}
          isLoading={isLoading}
          isCollapsed={isCollapsed}
          publicDynamicAssetsDomainName={publicDynamicAssetsDomainName}
        />

        <div className="relative">
          {organizationsVisible && (
            <Spacing
              direction="horizontal"
              size="xs"
              className="rounded-lg z-overlay absolute top-0"
            >
              <OrgSwitcherCard>
                <div className="min-h-0 max-h-[min(24rem,calc(100dvh-8rem))] overflow-y-auto">
                  <Spacing direction="vertical" size="small">
                    {organizations.map((org, index) => {
                      const databasesForThisOrg = getDatabasesForOrganization({
                        organizationId: org.id,
                        myOrganizationsDatabasesAndRevisions,
                      });

                      const hasMoreThanOneDatabaseForThisOrg =
                        databasesForThisOrg.length > 1;

                      const attachFloatingReference =
                        databaseSubmenuOpen &&
                        highlightedOrganization?.id === org.id &&
                        hasMoreThanOneDatabaseForThisOrg;

                      return (
                        <OrgSwitcherCardButton
                          key={org.id}
                          ref={
                            attachFloatingReference
                              ? refs.setReference
                              : undefined
                          }
                          variant="organization"
                          onClick={() => {
                            onOrganizationClick({
                              organizationId: org.id,
                              databasesForThisOrg,
                            });
                          }}
                          isHighlighted={highlightedOrganization?.id === org.id}
                          icon={
                            hasMoreThanOneDatabaseForThisOrg && <RightArrow />
                          }
                          text={org.name}
                          isFirst={index === 0}
                          isLast={index + 1 === numOrganizations}
                        />
                      );
                    })}
                  </Spacing>
                </div>
              </OrgSwitcherCard>
            </Spacing>
          )}

          {databaseSubmenuOpen && highlightedOrganization && (
            <FloatingPortal>
              <div
                ref={(node) => {
                  refs.setFloating(node);
                  databaseFloatingDomRef.current = node;
                }}
                style={floatingStyles}
                className="z-overlay rounded-md"
                {...getFloatingProps()}
              >
                <OrgSwitcherCard className="ml-0">
                  <Spacing direction="vertical" size="small">
                    {databasesForHighlightedOrganization.map((db, index) => {
                      return (
                        <OrgSwitcherCardButton
                          key={db.id}
                          variant="database"
                          onClick={() =>
                            onDatabaseClick({
                              databaseId: db.id,
                              organizationId: highlightedOrganization.id,
                            })
                          }
                          isHighlighted={selectedDatabase?.id === db.id}
                          text={db.name}
                          isFirst={index === 0}
                          isLast={
                            index + 1 === numDatabasesForHighlightedOrganization
                          }
                        />
                      );
                    })}
                  </Spacing>
                </OrgSwitcherCard>
              </div>
            </FloatingPortal>
          )}
        </div>
      </Spacing>
    </div>
  );
};
